import { useRef } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import {
  listConversationsOptions,
  getConversationOptions,
  getConversationRawOptions,
  getStatisticsOptions,
  toggleStarredMutation,
} from '@harmonyso/conversations-viewer-sdk';
import type {
  RawConversation,
} from '@/lib/types/conversations';
import type { Cell } from '@/lib/cells/cells';
import { cellQueryPolicy } from '@/lib/cells/cell-session';
import {
  conversationsViewerCellOptions,
  mergeCellStatistics,
} from '@/lib/cells/cell-statistics';
import { cellListPage, mergeCellConversations } from '@/lib/cells/cell-conversations';

/**
 * Fetch list of conversations from the database.
 * Returns paginated conversation metadata.
 */
export function useConversations(params?: {
  page?: number;
  page_size?: number;
  search?: string;
  source_identifier?: string;
  category?: string;
  ai_category?: string;
  workflow_type?: string;
  sentiment?: string;
  interest_level?: string;
  resolution?: string;
  source?: string;
  is_starred?: boolean;
  tenant_id?: string;
  min_turns?: number;
  max_turns?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  exclude_tenant_ids?: string[];
  date_from?: string;
  date_to?: string;
}) {
  // API call using SDK with additional params that SDK might not type yet
  const apiParams = {
    ...params,
    // Add sorting params if they exist (backend supports them even if SDK types don't yet)
    ...(params?.sortBy && { sort_by: params.sortBy }),
    ...(params?.sortDirection && { sort_direction: params.sortDirection }),
    ...(params?.exclude_tenant_ids && { exclude_tenant_ids: params.exclude_tenant_ids }),
    ...(params?.date_from && { date_from: params.date_from }),
    ...(params?.date_to && { date_to: params.date_to }),
  } as Record<string, unknown>;

  return useQuery({
    ...listConversationsOptions({ query: apiParams as never }),
    placeholderData: keepPreviousData,
  });
}

/**
 * One page of conversations from each of `cells`, merged (see
 * mergeCellConversations). `params.page` is 1-based. Each cell's request
 * leaves out `excludeTenantIds(cell)`.
 */
export function useCellConversations(
  cells: Cell[],
  params: Omit<Parameters<typeof useConversations>[0] & object, 'exclude_tenant_ids'> & {
    page: number;
    page_size: number;
    sortBy: string;
    sortDirection: 'asc' | 'desc';
  },
  excludeTenantIds: (cell: Cell) => string[],
) {
  const { sortBy, sortDirection, ...rest } = params;
  const pageRequest = cellListPage(cells.length, params.page, params.page_size);

  const result = useQueries({
    queries: cells.map((cell) => ({
      ...listConversationsOptions({
        ...conversationsViewerCellOptions(cell.id),
        query: {
          ...rest,
          ...pageRequest,
          sort_by: sortBy,
          sort_direction: sortDirection,
          exclude_tenant_ids: excludeTenantIds(cell),
        } as never,
      }),
      ...cellQueryPolicy(cell),
    })),
    combine: (results) => {
      const merged = mergeCellConversations(
        cells.map((cell, index) => ({
          cell,
          data: results.at(index)?.data,
          error: results.at(index)?.data ? null : results.at(index)?.error,
          isLoading: results.at(index)?.isPending,
        })),
        { page: params.page, pageSize: params.page_size, sortBy, sortDirection },
      );
      return {
        ...merged,
        isLoading: merged.loadingCells.length > 0,
        isRefetching: results.some((result) => result.isRefetching),
        refetch: () => {
          for (const result of results) void result.refetch();
        },
      };
    },
  });

  // useQueries ignores placeholderData, so a new page would blank the table
  // and its counts; show the last merged page until the new one is in. Only
  // for the same list (cells and filters): another list never shows this one.
  const listKey = JSON.stringify([cells.map(({ id }) => id), { ...rest, page: undefined, page_size: undefined }]);
  const lastLoaded = useRef<{ key: string; result: typeof result } | null>(null);
  if (!result.isLoading && cells.length > 0) {
    lastLoaded.current = { key: listKey, result };
  }
  if (result.isLoading && lastLoaded.current?.key === listKey) {
    return { ...lastLoaded.current.result, isLoading: false, isRefetching: true, refetch: result.refetch };
  }
  return result;
}

/**
 * Fetch detailed conversation with all turns from the database.
 * Requires tenant_id for authorization - uses env variable as fallback if not provided.
 */
export function useConversation(conversationId: string | null, tenantId: string | null = null) {
  // Use provided tenant_id or fallback to env variable
  const effectiveTenantId = tenantId || import.meta.env.VITE_HARMONY_TENANT_ID || null;

  return useQuery({
    ...getConversationOptions({
      path: { conversation_id: conversationId || '' },
      query: { tenant_id: effectiveTenantId || '' },
    }),
    enabled: !!conversationId && !!effectiveTenantId,
  });
}

/**
 * Fetch raw conversation JSON from S3 (presigned URL).
 * Used for "View Raw JSON" functionality.
 * Requires tenant_id for authorization - uses env variable as fallback if not provided.
 */
export function useConversationRaw(conversationId: string | null, tenantId: string | null = null) {
  // Use provided tenant_id or fallback to env variable
  const effectiveTenantId = tenantId || import.meta.env.VITE_HARMONY_TENANT_ID || null;

  const { data: rawUrlData } = useQuery({
    ...getConversationRawOptions({
      path: { conversation_id: conversationId || '' },
      query: { tenant_id: effectiveTenantId || '' },
    }),
    enabled: !!conversationId && !!effectiveTenantId,
  });

  return useQuery<RawConversation>({
    queryKey: ['conversation-raw', conversationId, effectiveTenantId, rawUrlData?.url],
    queryFn: async () => {
      if (!rawUrlData?.url) throw new Error('No raw URL available');
      const response = await axios.get<RawConversation>(rawUrlData.url);
      return response.data;
    },
    enabled: !!rawUrlData?.url,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Fetch conversation statistics and distributions.
 * Used for analytics dashboard.
 */
export function useConversationStatistics(params?: {
  tenant_id?: string;
  date_from?: string;
  date_to?: string;
  exclude_empty?: boolean;
  exclude_tenant_ids?: string[];
}) {
  // Convert ISO string dates to Date objects for the SDK
  const queryParams = params ? {
    tenant_id: params.tenant_id,
    date_from: params.date_from ? new Date(params.date_from) : undefined,
    date_to: params.date_to ? new Date(params.date_to) : undefined,
    exclude_empty: params.exclude_empty ?? true,
    ...(params.exclude_tenant_ids && { exclude_tenant_ids: params.exclude_tenant_ids }),
  } : undefined;

  return useQuery({
    ...getStatisticsOptions({ query: queryParams }),
  });
}

/**
 * Conversation statistics from each of `cells`, summed. `isLoading` stays
 * true until every cell has answered, so a partial total is never shown as
 * the whole. A cell that fails, including one without a session, is listed
 * in `failures` rather than dropped silently.
 */
export function useCellConversationStatistics(
  cells: Cell[],
  params: {
    tenant_id?: string;
    date_from?: string;
    date_to?: string;
    /** The tenants to leave out of each cell's statistics. */
    excludeTenantIds?: (cell: Cell) => string[];
  },
) {
  const query = {
    tenant_id: params.tenant_id,
    date_from: params.date_from ? new Date(params.date_from) : undefined,
    date_to: params.date_to ? new Date(params.date_to) : undefined,
    exclude_empty: true,
  };

  return useQueries({
    queries: cells.map((cell) => ({
      ...getStatisticsOptions({
        ...conversationsViewerCellOptions(cell.id),
        query: {
          ...query,
          ...(params.excludeTenantIds && { exclude_tenant_ids: params.excludeTenantIds(cell) }),
        },
      }),
      ...cellQueryPolicy(cell),
    })),
    combine: (results) => {
      const merged = mergeCellStatistics(
        cells.map((cell, index) => ({
          cell,
          stats: results.at(index)?.data,
          // A failed background refresh keeps the cell's numbers already loaded.
          error: results.at(index)?.data ? null : results.at(index)?.error,
          // isPending, not isLoading: a paused query must not count as answered.
          isLoading: results.at(index)?.isPending,
        })),
      );
      return { ...merged, isLoading: merged.loadingCells.length > 0 };
    },
  });
}

/**
 * Toggle the starred status of a conversation.
 * Invalidates related queries to refresh the UI.
 */
export function useToggleStarred() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId, tenantId }: { conversationId: string; tenantId: string }) => {
      const mutation = toggleStarredMutation();
      const mutationFn = mutation.mutationFn;
      if (!mutationFn) throw new Error('Mutation function not found');

      return mutationFn({
        path: { conversation_id: conversationId },
        query: { tenant_id: tenantId },
      });
    },
    onSuccess: () => {
      // Invalidate conversations list to refresh
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      // Invalidate conversation detail if open
      void queryClient.invalidateQueries({ queryKey: ['conversation'] });
    },
    onError: (error) => {
      console.error('Failed to toggle star:', error);
      toast.error('Failed to update star status');
    },
  });
}
